// Purpose: browser-side streaming encryptor producing the AST-CRYP format.
// Emits a ReadableStream<Uint8Array> that the caller pipes into a fetch body
// or a blob SAS upload. See docs/CRYPTO_FORMAT.md §7.

import {
  CHUNK_SIZE_BYTES,
  DEK_BYTES,
  FILE_SALT_BYTES,
  GCM_TAG_BYTES,
  LAST_CHUNK_FLAG,
} from './constants.js';
import { encodeHeader } from './header.js';
import { buildNonce } from './nonce.js';

export interface EncryptOptions {
  plaintextSize: number;
  salt?: Uint8Array;
}

// Encrypts the bytes flowing through `source` and returns a new stream of
// ciphertext bytes matching CRYPTO_FORMAT.md.
export function encryptStream(
  source: ReadableStream<Uint8Array>,
  dek: Uint8Array,
  opts: EncryptOptions
): ReadableStream<Uint8Array> {
  if (dek.length !== DEK_BYTES) {
    throw new Error(`crypto-web.encrypt: DEK must be ${DEK_BYTES} bytes`);
  }
  const salt = opts.salt ?? randomSalt();
  if (salt.length !== FILE_SALT_BYTES) {
    throw new Error(`crypto-web.encrypt: salt must be ${FILE_SALT_BYTES} bytes`);
  }

  const reader = source.getReader();
  let keyPromise: Promise<CryptoKey> | null = null;
  let plaintextBuf = new Uint8Array(0);
  let totalBytes = 0;
  let chunkIndex = 0n;
  let inputExhausted = false;
  let headerEmitted = false;
  let finished = false;

  function getKey(): Promise<CryptoKey> {
    if (!keyPromise) {
      keyPromise = crypto.subtle.importKey('raw', dek, { name: 'AES-GCM', length: 256 }, false, [
        'encrypt',
      ]);
    }
    return keyPromise;
  }

  async function emitChunk(
    controller: ReadableStreamDefaultController<Uint8Array>,
    chunkPlain: Uint8Array,
    isLast: boolean
  ): Promise<void> {
    const nonce = buildNonce(salt, chunkIndex);
    const key = await getKey();
    // WebCrypto AES-GCM output = ciphertext || tag.
    const out = new Uint8Array(
      await crypto.subtle.encrypt(
        {
          name: 'AES-GCM',
          iv: nonce,
          tagLength: GCM_TAG_BYTES * 8,
        },
        key,
        chunkPlain
      )
    );
    if (out.length !== chunkPlain.length + GCM_TAG_BYTES) {
      throw new Error('crypto-web.encrypt: unexpected AES-GCM output length');
    }

    const lenField = new Uint8Array(4);
    const rawLen = chunkPlain.length & 0x7fffffff;
    const encoded = isLast ? (rawLen | LAST_CHUNK_FLAG) >>> 0 : rawLen;
    new DataView(lenField.buffer).setUint32(0, encoded, false);
    controller.enqueue(lenField);
    controller.enqueue(out.subarray(0, chunkPlain.length));
    controller.enqueue(out.subarray(chunkPlain.length));
    chunkIndex += 1n;
  }

  return new ReadableStream<Uint8Array>({
    async pull(controller) {
      if (finished) {
        controller.close();
        return;
      }
      if (!headerEmitted) {
        const header = await encodeHeader(salt, opts.plaintextSize);
        controller.enqueue(header);
        headerEmitted = true;
      }

      while (plaintextBuf.length < CHUNK_SIZE_BYTES && !inputExhausted) {
        const { value, done } = await reader.read();
        if (done) {
          inputExhausted = true;
          break;
        }
        plaintextBuf = concat(plaintextBuf, value);
      }

      const takeLen = Math.min(CHUNK_SIZE_BYTES, plaintextBuf.length);
      const chunkPlain = plaintextBuf.subarray(0, takeLen);
      plaintextBuf = plaintextBuf.subarray(takeLen);
      totalBytes += takeLen;
      const isLast = inputExhausted && plaintextBuf.length === 0;

      if (takeLen === 0 && !isLast) return;

      await emitChunk(controller, chunkPlain, isLast);

      if (isLast) {
        if (totalBytes !== opts.plaintextSize) {
          controller.error(
            new Error(
              `crypto-web.encrypt: declared size ${opts.plaintextSize} but emitted ${totalBytes}`
            )
          );
          return;
        }
        finished = true;
        controller.close();
      }
    },
    cancel() {
      reader.cancel().catch(() => {});
    },
  });
}

function randomSalt(): Uint8Array {
  const s = new Uint8Array(FILE_SALT_BYTES);
  crypto.getRandomValues(s);
  return s;
}

function concat(a: Uint8Array, b: Uint8Array): Uint8Array {
  if (a.length === 0) return b;
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}
